const MAX_DEPTH = 6
const MAX_PREVIEW_CHARS = 40

// Connector tools routinely pass a JSON document as a string argument (Stripe's
// `parameters` is the common one). Left alone it renders as an escaped blob, so
// nested JSON strings are parsed back into values before display.
export function expandJsonStrings(value: unknown, depth = 0): unknown {
	if (depth >= MAX_DEPTH) return value
	if (typeof value === "string") {
		const parsed = tryParseJson(value)
		return parsed === undefined ? value : expandJsonStrings(parsed, depth + 1)
	}
	if (value === null || typeof value !== "object") return value
	if (Array.isArray(value)) {
		return value.map((item) => expandJsonStrings(item, depth + 1))
	}
	return Object.fromEntries(
		Object.entries(value as Record<string, unknown>).map(([key, item]) => [
			key,
			expandJsonStrings(item, depth + 1),
		]),
	)
}

function tryParseJson(value: string): unknown {
	const trimmed = value.trim()
	if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return undefined
	try {
		const parsed: unknown = JSON.parse(trimmed)
		return parsed !== null && typeof parsed === "object" ? parsed : undefined
	} catch {
		return undefined
	}
}

export function formatArgsJson(args: unknown): string {
	return JSON.stringify(expandJsonStrings(args), null, 2)
}

// A `{{...}}` token means the guard rendered this field, so the stored copy is the
// template rather than the values that went over the wire.
export function hasTemplateTokens(args: unknown): boolean {
	if (!args) return false
	return /\{\{[^}]+\}\}/.test(JSON.stringify(args) ?? "")
}

// One line for the list row, so a long payload never pushes the meta columns around.
export function summarizeArgs(args: unknown, argKeys: string[]): string {
	if (!args || typeof args !== "object" || Array.isArray(args)) {
		return argKeys.length > 0 ? argKeys.join(", ") : "no arguments"
	}
	const entries = Object.entries(
		expandJsonStrings(args) as Record<string, unknown>,
	)
	if (entries.length === 0) return "no arguments"
	return entries
		.map(([key, value]) => `${key}: ${previewValue(value)}`)
		.join(", ")
}

function previewValue(value: unknown): string {
	if (typeof value === "string") {
		return value.length > MAX_PREVIEW_CHARS
			? `"${value.slice(0, MAX_PREVIEW_CHARS)}..."`
			: `"${value}"`
	}
	if (value === null || typeof value !== "object") return String(value)
	if (Array.isArray(value)) return `[${value.length}]`
	const keys = Object.keys(value as Record<string, unknown>)
	return keys.length === 0 ? "{}" : `{ ${keys.join(", ")} }`
}
